import React, { useEffect } from 'react';
import { X } from 'lucide-react';

export default function PolicyModal({ type, onClose }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const content = type === 'privacy' ? <PrivacyContent /> : <TermsContent />;
  const title   = type === 'privacy' ? 'Privacy Policy' : 'Terms of Service';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="absolute inset-0 bg-[rgba(22,18,15,0.5)]"
        onClick={onClose}
      />
      <div
        className="relative bg-parchment w-full max-w-2xl max-h-[85vh] flex flex-col rounded-[4px] border border-border-warm"
        style={{ boxShadow: '0 2px 16px rgba(22,18,15,0.08)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-border-warm shrink-0">
          <h2 className="font-serif text-2xl font-normal text-ink-deep">{title}</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 text-ink-muted hover:text-ink-deep transition-colors"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-8 py-6 space-y-6 text-ink-body font-sans text-[0.9375rem] leading-[1.65]">
          {content}
        </div>
      </div>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <div>
      <h3 className="font-sans text-base font-medium text-ink-deep mb-2">
        {number}. {title}
      </h3>
      <div className="space-y-2 text-ink-muted">{children}</div>
    </div>
  );
}

function Sub({ title, children }) {
  return (
    <div className="mt-3">
      <p className="font-sans text-sm font-medium text-ink-body mb-1">{title}</p>
      <div className="text-ink-muted space-y-1">{children}</div>
    </div>
  );
}

function Li({ children }) {
  return (
    <p className="pl-4 before:content-['—'] before:mr-2 before:text-border-warm">
      {children}
    </p>
  );
}

function PrivacyContent() {
  return (
    <>
      <p className="text-ink-muted text-sm">
        <span className="font-medium text-ink-body">Last updated:</span> January 07, 2026
      </p>

      <Section number="1" title="Introduction">
        <p>
          This Privacy Policy explains how <strong className="text-ink-body">CTO Import and Distribution</strong>{' '}
          ("we", "our", or "us") collects, uses, and protects personal information when you visit{' '}
          <span className="text-ink-body">ctoimports.co</span>.
        </p>
      </Section>

      <Section number="2" title="Information We Collect">
        <Sub title="2.1 Information You Provide">
          <Li>Name</Li>
          <Li>Email address</Li>
          <Li>Company name (if provided)</Li>
          <Li>Message or inquiry content</Li>
          <p className="mt-2">
            This information is collected via contact forms and may be processed using services such as{' '}
            <strong className="text-ink-body">EmailJS</strong>.
          </p>
        </Sub>
        <Sub title="2.2 Automatically Collected Information">
          <p>
            We may collect technical data such as IP address, browser type, device information, pages visited,
            and approximate location.
          </p>
        </Sub>
      </Section>

      <Section number="3" title="Analytics and Tracking">
        <Li>Cloudflare Analytics</Li>
        <Li>Google Analytics</Li>
        <Li>Meta (Facebook) Pixel</Li>
      </Section>

      <Section number="4" title="Cookies">
        <p>
          Cookies and similar technologies may be used for functionality, analytics, security, and marketing
          purposes. You can control cookies through your browser settings.
        </p>
      </Section>

      <Section number="5" title="How We Use Your Information">
        <Li>Respond to inquiries</Li>
        <Li>Provide product and service information</Li>
        <Li>Improve website performance</Li>
        <Li>Measure marketing effectiveness</Li>
        <Li>Ensure security and prevent abuse</Li>
      </Section>

      <Section number="6" title="Data Sharing">
        <p>
          We do not sell personal data. Information may be shared with trusted service providers solely to
          operate our website and services.
        </p>
      </Section>

      <Section number="7" title="Data Retention">
        <p>
          Data is retained only as long as necessary for business or legal purposes. Analytics data may be
          stored in anonymized or aggregated form.
        </p>
      </Section>

      <Section number="8" title="Your Rights (USA)">
        <p>
          Depending on your state (including California under CCPA), you may request access to or deletion
          of your personal data.
        </p>
      </Section>

      <Section number="9" title="Children's Privacy">
        <p>
          This website is not intended for children under 13. We do not knowingly collect personal
          information from children.
        </p>
      </Section>

      <Section number="10" title="Contact">
        <p>CTO Import and Distribution</p>
        <p>Email: contact@ctoimports.co</p>
        <p>Address: 5450 Baneberry Ave, 43235 Columbus OH, USA</p>
      </Section>
    </>
  );
}

function TermsContent() {
  return (
    <>
      <p className="text-ink-muted text-sm">
        <span className="font-medium text-ink-body">Last updated:</span> January 07, 2026
      </p>

      <Section number="1" title="Acceptance of Terms">
        <p>
          By accessing or using the website of CTO Imports and Distribution ("Company", "we", "our", or "us"),
          you agree to be bound by these Terms of Service. If you do not agree, please do not use this website.
        </p>
      </Section>

      <Section number="2" title="Purpose of the Website">
        <p>
          This website is provided for informational purposes only. It displays products imported and
          distributed by the Company and allows users to submit inquiries or contact requests. No purchases
          or contracts are formed through this website.
        </p>
      </Section>

      <Section number="3" title="Use of the Website">
        <p>You agree to use this website only for lawful purposes and not to:</p>
        <Li>Violate applicable laws or regulations</Li>
        <Li>Infringe the rights of others</Li>
        <Li>Attempt unauthorized access to systems or data</Li>
        <Li>Introduce malicious software or code</Li>
      </Section>

      <Section number="4" title="Intellectual Property">
        <p>
          All content on this website, including text, images, logos, trademarks, and design elements, is
          the property of the Company or its licensors and is protected by applicable intellectual property
          laws.
        </p>
      </Section>

      <Section number="5" title="Product Information Disclaimer">
        <p>
          Product descriptions, images, and availability are provided for general informational purposes
          only. We do not guarantee accuracy or completeness and reserve the right to modify information
          at any time. The Company, as an importer and distributor, expressly disclaims all liability
          for product defects, personal injury, or property damage arising from the use of any product.
          Product liability, where applicable, rests solely with the manufacturer.
        </p>
      </Section>

      <Section number="6" title="User Submissions">
        <p>
          When submitting information via forms, you represent that the information is accurate and that
          you have the right to provide it. Submitted data may be used to respond to inquiries and for
          legitimate business purposes.
        </p>
      </Section>

      <Section number="7" title="Third-Party Services">
        <p>
          This website may use third-party services such as Cloudflare Analytics, Google Analytics, Meta
          Pixel, and EmailJS. We are not responsible for the content or practices of third-party services.
        </p>
      </Section>

      <Section number="8" title="Disclaimer of Warranties">
        <p>
          This website is provided "as is" and "as available" without warranties of any kind. We do not
          guarantee uninterrupted or error-free operation.
        </p>
      </Section>

      <Section number="9" title="Limitation of Liability">
        <p>
          To the fullest extent permitted by law, the Company shall not be liable for any indirect,
          incidental, or consequential damages arising from the use of this website.
        </p>
      </Section>

      <Section number="10" title="Indemnification">
        <p>
          You agree to indemnify and hold harmless the Company from claims arising from your use of the
          website or violation of these Terms.
        </p>
      </Section>

      <Section number="11" title="Governing Law and Jurisdiction">
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the State of Ohio,
          United States, without regard to its conflict of law provisions. Any disputes arising out of or
          relating to these Terms or your use of this website shall be subject to the exclusive jurisdiction
          of the state and federal courts located in Franklin County, Ohio, and you hereby consent to
          personal jurisdiction in those courts.
        </p>
      </Section>

      <Section number="12" title="Force Majeure">
        <p>
          The Company shall not be liable for any failure or delay in performance resulting from causes
          beyond its reasonable control, including but not limited to acts of God, natural disasters,
          pandemics, war, terrorism, labor disputes, government actions, customs delays, import/export
          restrictions, or supply chain disruptions.
        </p>
      </Section>

      <Section number="13" title="International Users and GDPR">
        <p>
          This website is operated from the United States. If you are accessing it from the European Union
          or other regions with laws governing data collection and use, please be aware that your
          information may be transferred to and processed in the United States. By using this website, you
          consent to that transfer. Users in the EU may have additional rights under the General Data
          Protection Regulation (GDPR); please contact us at contact@ctoimports.co to exercise those rights.
        </p>
      </Section>

      <Section number="14" title="Severability">
        <p>
          If any provision of these Terms is found to be unlawful, void, or unenforceable, that provision
          shall be deemed severable and shall not affect the validity and enforceability of the remaining
          provisions, which shall continue in full force and effect.
        </p>
      </Section>

      <Section number="15" title="Changes to These Terms">
        <p>
          We reserve the right to update these Terms at any time. Continued use of the website constitutes
          acceptance of any changes. We encourage you to review these Terms periodically.
        </p>
      </Section>

      <Section number="16" title="Contact Information">
        <p>CTO Import and Distribution</p>
        <p>Email: contact@ctoimports.co</p>
        <p>Address: 5450 Baneberry Ave, 43235 Columbus OH, USA</p>
      </Section>

      <p className="text-ink-muted/70 text-xs italic pt-2 border-t border-border-warm">
        These Terms of Service are provided for informational purposes and do not constitute legal advice.
        We recommend consulting a qualified attorney for advice specific to your situation.
      </p>
    </>
  );
}
